import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Calendar, MapPin, Ticket, Users, Tag, ArrowLeft, CheckCircle } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import api from "../api";
import "./EventDetail.css";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// ── Helpers ──────────────────────────────────────────────────────────────────

function resolveImageUrl(image) {
  if (!image) return null;
  if (image.startsWith("http")) return image;
  return `${process.env.REACT_APP_API_URL.replace("/api/", "")}${image}`;
}

// ── Package Selector ─────────────────────────────────────────────────────────

function PackageSelector({ packages, selectedPackage, onSelect, onClear }) {
  return (
    <div className="ed-package-selector">
      <h4 className="ed-package-title">🎫 Choose a Package</h4>
      <p className="ed-package-sub">
        Select a named group package below, or scroll down to buy individual tickets.
      </p>
      <div className="ed-packages-grid">
        {packages.map(pkg => (
          <button
            key={pkg.id}
            type="button"
            className={`ed-package-card${selectedPackage?.id === pkg.id ? " selected" : ""}${pkg.is_sold_out ? " sold-out" : ""}`}
            onClick={() => !pkg.is_sold_out && onSelect(pkg)}
            disabled={pkg.is_sold_out}
          >
            <div className="ed-pkg-name">{pkg.name}</div>
            <div className="ed-pkg-size">{pkg.size} people</div>
            <div className="ed-pkg-price">${parseFloat(pkg.price).toFixed(2)} AUD</div>
            <div className="ed-pkg-per">${pkg.price_per_person.toFixed(2)} per person</div>
            {pkg.description && <div className="ed-pkg-desc">{pkg.description}</div>}
            {pkg.is_sold_out && <div className="ed-pkg-soldout">Sold Out</div>}
            {selectedPackage?.id === pkg.id && <div className="ed-pkg-tick">✓ Selected</div>}
          </button>
        ))}
      </div>
      {selectedPackage && (
        <button type="button" className="ed-clear-pkg" onClick={onClear}>
          ✕ Remove package — buy individual tickets instead
        </button>
      )}
      <div className="ed-pkg-divider"><span>— or buy individual tickets below —</span></div>
    </div>
  );
}

// ── Pricing Tiers ────────────────────────────────────────────────────────────

function PricingTiers({ event }) {
  if (!event.tier2_min && !event.tier3_min) return null;
  return (
    <div className="ed-pricing-tiers">
      <p className="ed-tiers-title"><Tag size={14} /> Group Discounts</p>
      <div className="ed-tiers-grid">
        <div className="ed-tier-row">
          <span>1{event.tier2_min ? `–${event.tier2_min - 1}` : "+"} tickets</span>
          <span>${parseFloat(event.ticket_price).toFixed(2)} each</span>
          <span className="ed-tier-tag">Standard</span>
        </div>
        {event.tier2_min && (
          <div className="ed-tier-row">
            <span>{event.tier2_min}{event.tier3_min ? `–${event.tier3_min - 1}` : "+"} tickets</span>
            <span>${(parseFloat(event.ticket_price) * (1 - parseFloat(event.tier2_discount) / 100)).toFixed(2)} each</span>
            <span className="ed-tier-tag green">{event.tier2_discount}% off</span>
          </div>
        )}
        {event.tier3_min && (
          <div className="ed-tier-row">
            <span>{event.tier3_min}+ tickets</span>
            <span>${(parseFloat(event.ticket_price) * (1 - parseFloat(event.tier3_discount) / 100)).toFixed(2)} each</span>
            <span className="ed-tier-tag gold">{event.tier3_discount}% off</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Attendee Fields ───────────────────────────────────────────────────────────

function AttendeeFields({ quantity, attendees, onChange }) {
  return (
    <div className="ed-attendees">
      <h4 className="ed-attendees-title">
        👥 Attendee Details ({quantity} {quantity === 1 ? "person" : "people"})
      </h4>
      <p className="ed-attendee-note">#1 is pre-filled from your booking contact details above.</p>
      {attendees.map((attendee, i) => (
        <div key={i} className="ed-attendee-row">
          <div className="ed-attendee-num">{i === 0 ? "📋 #1" : `#${i + 1}`}</div>
          <div className="ed-attendee-fields">
            <div className="ed-form-row">
              <div className="ed-field">
                <label>First Name</label>
                <input
                  value={attendee.first_name}
                  onChange={e => onChange(i, 'first_name', e.target.value)}
                  placeholder="First name"
                  className={attendee._error_first ? "error" : ""}
                  readOnly={i === 0}
                  style={i === 0 ? { background: "#f5f5f5", color: "#666" } : {}}
                />
                {attendee._error_first && <span className="ed-err">{attendee._error_first}</span>}
              </div>
              <div className="ed-field">
                <label>Last Name</label>
                <input
                  value={attendee.last_name}
                  onChange={e => onChange(i, 'last_name', e.target.value)}
                  placeholder="Last name"
                  className={attendee._error_last ? "error" : ""}
                  readOnly={i === 0}
                  style={i === 0 ? { background: "#f5f5f5", color: "#666" } : {}}
                />
                {attendee._error_last && <span className="ed-err">{attendee._error_last}</span>}
              </div>
            </div>
            <div className="ed-field">
              <label>Email {i === 0 ? "" : "(optional)"}</label>
              <input
                type="email"
                value={attendee.email}
                onChange={e => onChange(i, 'email', e.target.value)}
                placeholder={i === 0 ? "email@example.com" : "email@example.com (optional)"}
                className={attendee._error_email ? "error" : ""}
                readOnly={i === 0}
                style={i === 0 ? { background: "#f5f5f5", color: "#666" } : {}}
              />
              {attendee._error_email && <span className="ed-err">{attendee._error_email}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Ticket Form ───────────────────────────────────────────────────────────────

function TicketForm({ event, onSuccess }) {
  const stripe   = useStripe();
  const elements = useElements();

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", phone: "", quantity: 1,
  });
  const emptyAttendee = () => ({
    first_name: "", last_name: "", email: "",
    _error_first: "", _error_last: "", _error_email: "",
  });
  const [attendees,  setAttendees]  = useState([emptyAttendee()]);
  const [errors,     setErrors]     = useState({});
  const [loading,    setLoading]    = useState(false);
  const [cardError,  setCardError]  = useState("");
  const [pricing,    setPricing]    = useState({
    unitPrice: parseFloat(event.ticket_price || 0),
    discountPct: 0,
    total: parseFloat(event.ticket_price || 0),
  });

  const syncAttendeeOne = (field, value) => {
    setAttendees(prev => {
      const u = [...prev];
      u[0] = { ...u[0], [field]: value };
      return u;
    });
  };

  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
    const qty = pkg.size;
    setPricing({ unitPrice: pkg.price_per_person, discountPct: 0, total: parseFloat(pkg.price) });
    setFormData(prev => ({ ...prev, quantity: qty }));
    setAttendees(() => {
      const list = Array(qty).fill(null).map(emptyAttendee);
      list[0] = { ...emptyAttendee(), first_name: formData.first_name, last_name: formData.last_name, email: formData.email };
      return list;
    });
  };

  const handleClearPackage = () => { setSelectedPackage(null); updateQuantity(1); };

  const updateQuantity = (qty) => {
    if (selectedPackage) return;
    const q = Math.max(1, Math.min(50, parseInt(qty) || 1));
    let discountPct = 0;
    if (event.tier3_min && q >= event.tier3_min && event.tier3_discount)
      discountPct = parseFloat(event.tier3_discount);
    else if (event.tier2_min && q >= event.tier2_min && event.tier2_discount)
      discountPct = parseFloat(event.tier2_discount);
    const base = parseFloat(event.ticket_price);
    const unitPrice = parseFloat((base * (1 - discountPct / 100)).toFixed(2));
    const total = parseFloat((unitPrice * q).toFixed(2));
    setPricing({ unitPrice, discountPct, total });
    setFormData(prev => ({ ...prev, quantity: q }));
    setAttendees(prev => {
      if (q > prev.length) return [...prev, ...Array(q - prev.length).fill(null).map(emptyAttendee)];
      return prev.slice(0, q);
    });
  };

  const handleAttendeeChange = (index, field, value) => {
    if (index === 0) return;
    setAttendees(prev => {
      const u = [...prev];
      const key = field === 'first_name' ? '_error_first' : field === 'last_name' ? '_error_last' : '_error_email';
      u[index] = { ...u[index], [field]: value, [key]: "" };
      return u;
    });
  };

  const validate = () => {
    const e = {};
    if (!formData.first_name.trim()) e.first_name = "Required";
    if (!formData.last_name.trim())  e.last_name  = "Required";
    if (!formData.email.trim())      e.email      = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Invalid email";
    setErrors(e);

    let attendeesValid = true;
    const updated = attendees.map((a, i) => {
      const errs = { _error_first: "", _error_last: "", _error_email: "" };
      if (!a.first_name.trim()) { errs._error_first = "Required"; attendeesValid = false; }
      if (!a.last_name.trim())  { errs._error_last  = "Required"; attendeesValid = false; }
      if (i > 0 && a.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email)) {
        errs._error_email = "Invalid email format"; attendeesValid = false;
      }
      return { ...a, ...errs };
    });
    setAttendees(updated);
    return Object.keys(e).length === 0 && attendeesValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate() || !stripe || !elements) return;
    setLoading(true);
    setCardError("");

    const cleanAttendees = attendees.map(({ _error_first, _error_last, _error_email, ...a }) => a);

    try {
      const intentResp = await api.post("events/tickets/create-intent/", {
        event_id: event.id,
        package_id: selectedPackage?.id || null,
        first_name: formData.first_name,
        last_name:  formData.last_name,
        email:      formData.email,
        phone:      formData.phone,
        quantity:   formData.quantity,
      });

      const { client_secret, payment_intent_id } = intentResp.data;

      const result = await stripe.confirmCardPayment(client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name:  `${formData.first_name} ${formData.last_name}`,
            email: formData.email,
          },
        },
      });

      if (result.error) { setCardError(result.error.message); setLoading(false); return; }

      const confirmResp = await api.post("events/tickets/confirm/", {
        payment_intent_id,
        event_id:   event.id,
        package_id: selectedPackage?.id || null,
        first_name: formData.first_name,
        last_name:  formData.last_name,
        email:      formData.email,
        phone:      formData.phone,
        quantity:   formData.quantity,
        attendees:  cleanAttendees,
      });

      onSuccess({
        ...confirmResp.data,
        buyer_name:  `${formData.first_name} ${formData.last_name}`,
        buyer_email: formData.email,
        attendees:   cleanAttendees,
      });

    } catch (err) {
      setCardError(err.response?.data?.error || "Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const activePackages = (event.packages || []).filter(p => p.is_active);

  return (
    <div className="ed-ticket-form-wrapper" id="buy-ticket">
      <h2 className="ed-section-heading">🎟 Book Tickets</h2>

      {activePackages.length > 0 && (
        <PackageSelector
          packages={activePackages}
          selectedPackage={selectedPackage}
          onSelect={handleSelectPackage}
          onClear={handleClearPackage}
        />
      )}

      {!selectedPackage && <PricingTiers event={event} />}

      <form onSubmit={handleSubmit} className="ed-form">
        {selectedPackage && (
          <div className="ed-selected-banner">
            🎫 <strong>{selectedPackage.name}</strong> — {selectedPackage.size} people
            at ${parseFloat(selectedPackage.price).toFixed(2)} AUD
          </div>
        )}

        {/* Booking contact */}
        <div className="ed-booker-section">
          <h4 className="ed-sub-heading">📋 Booking Contact</h4>
          <div className="ed-form-row">
            <div className="ed-field">
              <label>First Name</label>
              <input
                value={formData.first_name}
                onChange={e => {
                  const v = e.target.value;
                  setFormData(p => ({ ...p, first_name: v }));
                  if (errors.first_name) setErrors(p => ({ ...p, first_name: "" }));
                  syncAttendeeOne('first_name', v);
                }}
                className={errors.first_name ? "error" : ""}
              />
              {errors.first_name && <span className="ed-err">{errors.first_name}</span>}
            </div>
            <div className="ed-field">
              <label>Last Name</label>
              <input
                value={formData.last_name}
                onChange={e => {
                  const v = e.target.value;
                  setFormData(p => ({ ...p, last_name: v }));
                  if (errors.last_name) setErrors(p => ({ ...p, last_name: "" }));
                  syncAttendeeOne('last_name', v);
                }}
                className={errors.last_name ? "error" : ""}
              />
              {errors.last_name && <span className="ed-err">{errors.last_name}</span>}
            </div>
          </div>
          <div className="ed-form-row">
            <div className="ed-field">
              <label>Email (confirmation sent here)</label>
              <input
                type="email"
                value={formData.email}
                onChange={e => {
                  const v = e.target.value;
                  setFormData(p => ({ ...p, email: v }));
                  if (errors.email) setErrors(p => ({ ...p, email: "" }));
                  syncAttendeeOne('email', v);
                }}
                className={errors.email ? "error" : ""}
              />
              {errors.email && <span className="ed-err">{errors.email}</span>}
            </div>
            <div className="ed-field">
              <label>Phone (optional)</label>
              <input
                value={formData.phone}
                onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                placeholder="+61 400 000 000"
              />
            </div>
          </div>
        </div>

        {/* Quantity */}
        {!selectedPackage && (
          <div className="ed-qty-section">
            <div className="ed-field">
              <label>Number of Tickets</label>
              <div className="ed-qty-row">
                <button type="button" className="ed-qty-btn"
                  onClick={() => updateQuantity((parseInt(formData.quantity) || 1) - 1)}>−</button>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formData.quantity}
                  onChange={e => {
                    const v = e.target.value.replace(/[^0-9]/g, "");
                    if (v === "") setFormData(p => ({ ...p, quantity: "" }));
                    else updateQuantity(parseInt(v));
                  }}
                  onBlur={() => { if (!formData.quantity || formData.quantity < 1) updateQuantity(1); }}
                  className="ed-qty-input"
                />
                <button type="button" className="ed-qty-btn"
                  onClick={() => updateQuantity((parseInt(formData.quantity) || 1) + 1)}>+</button>
              </div>
            </div>
            <div className="ed-price-summary">
              {pricing.discountPct > 0 && (
                <div className="ed-discount-badge">🎉 {pricing.discountPct}% group discount applied!</div>
              )}
              <div className="ed-total-row">
                <span>{formData.quantity} × ${pricing.unitPrice.toFixed(2)}</span>
                <strong>${pricing.total.toFixed(2)} AUD</strong>
              </div>
            </div>
          </div>
        )}

        {selectedPackage && (
          <div className="ed-total-row" style={{ marginBottom: "1rem" }}>
            <span>{selectedPackage.size} people × ${selectedPackage.price_per_person.toFixed(2)}</span>
            <strong>${parseFloat(selectedPackage.price).toFixed(2)} AUD</strong>
          </div>
        )}

        <AttendeeFields
          quantity={formData.quantity}
          attendees={attendees}
          onChange={handleAttendeeChange}
        />

        <div className="ed-field" style={{ marginTop: "1rem" }}>
          <label>Card Details</label>
          <div className="ed-card-wrapper">
            <CardElement options={{
              hidePostalCode: true,
              style: {
                base:    { fontSize: "16px", color: "#333", "::placeholder": { color: "#aaa" } },
                invalid: { color: "#e53935" },
              }
            }} />
          </div>
          {cardError && <span className="ed-err">{cardError}</span>}
        </div>

        <p className="ed-secure-note">🔒 Secured by Stripe. Your card details are encrypted.</p>

        <button type="submit" className="ed-pay-btn" disabled={loading || !stripe}>
          {loading
            ? "Processing…"
            : `Pay $${selectedPackage ? parseFloat(selectedPackage.price).toFixed(2) : pricing.total.toFixed(2)} AUD`}
        </button>
      </form>
    </div>
  );
}

// ── Success Screen ────────────────────────────────────────────────────────────

function TicketSuccess({ data, onReset }) {
  return (
    <div className="ed-success">
      <CheckCircle size={64} color="#28a745" style={{ display: "block", margin: "0 auto 1rem" }} />
      <h2>Tickets Confirmed! 🎉</h2>
      <p>Hi <strong>{data.buyer_name}</strong>, your booking is confirmed.</p>

      <div className="ed-success-details">
        <div><span>Event</span><strong>{data.event_title}</strong></div>
        <div><span>Date</span><strong>{data.event_date}</strong></div>
        {data.package_name && <div><span>Package</span><strong>{data.package_name}</strong></div>}
        <div><span>Tickets</span><strong>{data.quantity}</strong></div>
        {data.discount_pct > 0 && (
          <div><span>Discount</span><strong style={{ color: "#28a745" }}>{data.discount_pct}% off</strong></div>
        )}
        <div><span>Total Paid</span><strong>${parseFloat(data.total_amount).toFixed(2)} AUD</strong></div>
        <div><span>Reference</span><strong className="ed-ref">{data.ticket_reference}</strong></div>
      </div>

      {data.attendees && data.attendees.length > 0 && (
        <div className="ed-attendees-summary">
          <p className="ed-attendees-summary-title">Attendees:</p>
          {data.attendees.map((a, i) => (
            <div key={i} className="ed-attendee-summary-row">
              <span>{i + 1}. {a.first_name} {a.last_name}</span>
              <span>{a.email}</span>
            </div>
          ))}
        </div>
      )}

      <p className="ed-email-note">
        Confirmation sent to <strong>{data.buyer_email}</strong>.
        Show your reference at the event.
      </p>
      <button className="ed-done-btn" onClick={onReset}>Book more tickets</button>
    </div>
  );
}

// ── Main Event Detail Page ────────────────────────────────────────────────────

export default function EventDetail() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [event,    setEvent]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [success,  setSuccess]  = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [copied,   setCopied]   = useState(false);

  useEffect(() => {
    api.get(`events/${slug}/`)
      .then(res => {
        setEvent(res.data);
        // Auto-open the ticket form if ?buy=1 is in the URL
        if (searchParams.get("buy") === "1") {
          setShowForm(true);
          // Scroll to form after a brief render delay
          setTimeout(() => {
            document.getElementById("buy-ticket")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 150);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  const ticketLink = `${window.location.origin}/events/${slug}?buy=1`;

  const copyTicketLink = () => {
    navigator.clipboard.writeText(ticketLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  if (loading) return (
    <div className="ed-loading">Loading event…</div>
  );

  if (notFound || !event) return (
    <div className="ed-not-found">
      <h2>Event not found</h2>
      <button className="ed-back-btn" onClick={() => navigate("/events")}>
        <ArrowLeft size={16} /> Back to Events
      </button>
    </div>
  );

  const past        = new Date(event.date) < new Date();
  const imageUrl    = resolveImageUrl(event.image);
  const hasPackages = (event.packages || []).filter(p => p.is_active).length > 0;

  return (
    <div className="ed-page">

      {/* Hero image */}
      {imageUrl && (
        <div className="ed-hero">
          <img src={imageUrl} alt={event.title} />
          <div className="ed-hero-overlay" />
          <div className="ed-hero-badges">
            {event.is_paid_event && !past && (
              <span className="ed-badge green">🎟 Ticketed Event</span>
            )}
            {hasPackages && !past && (
              <span className="ed-badge gold">🎫 Group Packages</span>
            )}
            {past && (
              <span className="ed-badge grey">Past Event</span>
            )}
          </div>
          <button className="ed-back-hero" onClick={() => navigate("/events")}>
            <ArrowLeft size={16} /> All Events
          </button>
        </div>
      )}

      <div className="ed-content">

        {/* Back link (if no hero) */}
        {!imageUrl && (
          <button className="ed-back-btn" onClick={() => navigate("/events")}>
            <ArrowLeft size={16} /> Back to Events
          </button>
        )}

        {/* Title + Meta */}
        <div className="ed-header">
          <h1 className="ed-title">{event.title}</h1>
          <div className="ed-meta">
            <div className="ed-meta-item">
              <Calendar size={18} />
              <span>{event.date}{event.time ? ` · ${event.time}` : ""}</span>
            </div>
            <div className="ed-meta-item">
              <MapPin size={18} />
              <span>{event.location || "TBC"}</span>
            </div>
            {event.is_paid_event && event.ticket_price && (
              <div className="ed-meta-item">
                <Ticket size={18} />
                <span>${event.ticket_price} AUD per ticket</span>
              </div>
            )}
            {event.is_paid_event && event.total_tickets && (
              <div className="ed-meta-item">
                <Users size={18} />
                {event.is_sold_out
                  ? <span className="ed-sold-out">Sold Out</span>
                  : <span>{event.tickets_remaining} tickets left</span>}
              </div>
            )}
            {hasPackages && (
              <div className="ed-meta-item">
                <Ticket size={18} />
                <span>{(event.packages || []).filter(p => p.is_active).length} group packages available</span>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {event.description && (
          <div className="ed-description">
            <h2 className="ed-section-heading">About this event</h2>
            <p>{event.description}</p>
          </div>
        )}

        {/* Free event note */}
        {!event.is_paid_event && !past && (
          <div className="ed-free-note">
            ✅ This is a <strong>free event</strong> — all are welcome, just show up!
          </div>
        )}

        {/* Ticket CTA / form / success */}
        {event.is_paid_event && !past && !event.is_sold_out && (
          success
            ? <TicketSuccess data={success} onReset={() => { setSuccess(null); setShowForm(false); setSearchParams({}); }} />
            : showForm
              ? (
                <Elements stripe={stripePromise}>
                  <TicketForm event={event} onSuccess={setSuccess} />
                </Elements>
              )
              : (
                <div className="ed-buy-cta">
                  <div className="ed-buy-cta-info">
                    <Ticket size={20} />
                    <span>
                      <strong>${event.ticket_price} AUD</strong> per ticket
                      {event.total_tickets && !event.is_sold_out && (
                        <span className="ed-cta-remaining"> · {event.tickets_remaining} left</span>
                      )}
                    </span>
                  </div>
                  <div className="ed-cta-actions">
                    <button
                      className="ed-buy-btn"
                      onClick={() => {
                        setShowForm(true);
                        setTimeout(() => {
                          document.getElementById("buy-ticket")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }, 50);
                      }}
                    >
                      🎟 Buy Ticket
                    </button>
                    <button
                      className="ed-share-btn"
                      onClick={copyTicketLink}
                      title={ticketLink}
                    >
                      {copied ? "✅ Copied!" : "🔗 Copy Ticket Link"}
                    </button>
                  </div>
                </div>
              )
        )}

        {event.is_paid_event && !past && event.is_sold_out && (
          <div className="ed-soldout-box">
            <Ticket size={24} />
            <div>
              <strong>This event is sold out</strong>
              <p>Check back later or explore other upcoming events.</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}